'use client';

/** @jsxImportSource @emotion/react */

import { useCallback, useMemo } from 'react';

import {
  Button,
  Container,
  FlexItem,
  FlexLayout,
  Icon,
  LazyLottieBackground,
  Typography,
} from '@wci-tech/core-ui';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { BsArrowUpRight } from 'react-icons/bs';
import { MdOutlineKeyboardDoubleArrowRight } from 'react-icons/md';

import { BookADemoButton } from '@/components';
import { appConfig } from '@/config/appConfig';
import { useThemeMode } from '@/context';

import { useStyles } from './PlatformHeroBanner.styles';

const DynamicBreadcrumbsWithNoSSR = dynamic(
  () => import('@wci-tech/core-ui').then((mod) => mod.Breadcrumbs),
  {
    ssr: false,
    loading: () => null,
  },
);

type BreadcrumbItem = {
  label: string;
  href: string;
};

type NonEmptyArray<T> = [T, ...T[]];

type ResponsiveHeroAssets = {
  mobile: string;
  tablet: string;
  desktop: string;
};

type HeroThemeAssets = {
  lottie: ResponsiveHeroAssets;
  placeholder: ResponsiveHeroAssets;
};

const HERO_ASSETS: Record<'light' | 'dark', HeroThemeAssets> = {
  light: {
    lottie: {
      mobile: '/animations/mobile-light.lottie',
      tablet: '/animations/tablet-light.lottie',
      desktop: '/animations/desktop-light.lottie',
    },
    placeholder: {
      mobile: '/images/heroLottieMobileLight.png',
      tablet: '/images/heroLottieTabletLight.png',
      desktop: '/images/heroLottieDesktopLight.png',
    },
  },
  dark: {
    lottie: {
      mobile: '/animations/mobile-dark.lottie',
      tablet: '/animations/tablet-dark.lottie',
      desktop: '/animations/desktop-dark.lottie',
    },
    placeholder: {
      mobile: '/images/heroLottieMobile.png',
      tablet: '/images/heroLottieTablet.png',
      desktop: '/images/heroLottieDesktop.png',
    },
  },
};

const HeroSection = () => {
  const pathname = usePathname();
  const router = useRouter();
  const { isDark } = useThemeMode();

  const styles = useStyles(isDark);
  const t = useTranslations('Header');
  const tHero = useTranslations('PlatformHero');

  const pages = useMemo<Record<string, NonEmptyArray<BreadcrumbItem>>>(
    () => ({
      '/': [{ label: tHero('breadcrumbs.home'), href: '/' }],
      '/platform': [
        { label: tHero('breadcrumbs.home'), href: '/' },
        { label: tHero('breadcrumbs.platform'), href: '/platform' },
      ],
    }),
    [tHero],
  );

  const breadcrumbItems = pages[pathname] ?? pages['/'];

  const navigate = useCallback(
    (path: string) => {
      router.push(path);
    },
    [router],
  );

  const heroAssets = isDark ? HERO_ASSETS.dark : HERO_ASSETS.light;

  return (
    <section css={styles.wrapper}>
      <LazyLottieBackground
        responsiveLottieSrc={heroAssets.lottie}
        responsivePlaceholderImageSrc={heroAssets.placeholder}
        customStyles={{
          wrapper: styles.lottieWrapper,
          lottie: styles.lottieMedia,
          placeholder: styles.lottieMedia,
          lottieContainer: styles.lottieLayer,
        }}
      >
        <Container css={styles.container}>
          <div css={styles.breadcrumbWrapper}>
            <DynamicBreadcrumbsWithNoSSR
              items={breadcrumbItems}
              navigate={navigate}
              separator={
                <Icon
                  name={MdOutlineKeyboardDoubleArrowRight}
                  color={isDark ? '#f5f6fa' : '#333333'}
                />
              }
              variant={isDark ? 'secondary' : 'primary'}
            />
          </div>

          <FlexLayout
            direction="column"
            alignItems="center"
            padding={{ default: 0 }}
            justifyContent="center"
            customStyles={{
              flexContainer: styles.contentArea,
            }}
          >
            <FlexItem>
              <FlexLayout
                direction="column"
                alignItems="center"
                padding={{ default: 0 }}
                justifyContent="center"
                gap={{ default: 36, md: 40, lg: 56 }}
                customStyles={{
                  flexContainer: styles.contentWrapper,
                }}
              >
                <FlexItem
                  customStyles={{
                    flexItem: styles.headerSection,
                  }}
                >
                  <FlexLayout
                    direction="column"
                    padding={{ default: 0 }}
                    alignItems="center"
                    gap={16}
                  >
                    <Typography variant="h1" customStyles={styles.title}>
                      {tHero('title')}
                    </Typography>

                    <Typography variant="p" customStyles={styles.description}>
                      {tHero('description')}
                    </Typography>
                  </FlexLayout>
                </FlexItem>

                <FlexItem
                  customStyles={{
                    flexItem: styles.buttonSection,
                  }}
                >
                  <FlexLayout
                    direction={{ default: 'column', md: 'row' }}
                    padding={{ default: 0 }}
                    justifyContent="center"
                    gap={{ default: 16, md: 24 }}
                  >
                    <FlexItem>
                      <BookADemoButton
                        shape="pill"
                        variant="linear-gradient"
                        buttonText={t('bookDemo')}
                        customStyles={{
                          base: styles.bookDemoBtn,
                        }}
                        calendlyUrl={appConfig.integrations.calendly.eventUrl}
                      />
                    </FlexItem>

                    <FlexItem>
                      <Button
                        appearance="outline"
                        shape="pill"
                        variant={isDark ? 'secondary' : 'primary'}
                        css={styles.viewDocBtn}
                        href="/coming-soon"
                        effect="icon-shift"
                        iconPlacement="right"
                        icon={
                          <Icon
                            name={BsArrowUpRight}
                            size={{
                              default: '14px',
                              lg: '16px',
                            }}
                          />
                        }
                        customStyles={{
                          icon: {
                            right: styles.btnIcon,
                          },
                        }}
                      >
                        {tHero('buttons.viewDocs')}
                      </Button>
                    </FlexItem>
                  </FlexLayout>
                </FlexItem>
              </FlexLayout>
            </FlexItem>
          </FlexLayout>
        </Container>
      </LazyLottieBackground>
    </section>
  );
};

export default HeroSection;
